import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  approvalRulesApi,
  approverApi,
  clientVisitApi,
  correctionsApi,
  orgChartApi,
  overtimeApi,
} from "../workflowApi";

const useKeysMutation = (fn, keys) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () =>
      keys.forEach((queryKey) => qc.invalidateQueries({ queryKey })),
  });
};

/* ---------- Approver chains ---------- */
export const useApproverList = (filters) =>
  useQuery({
    queryKey: ["approvers", filters],
    queryFn: () => approverApi.getList(filters),
    placeholderData: keepPreviousData,
  });
export const useEmployeeApprovers = (employeeId, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["approvers", "employee", employeeId],
    queryFn: () => approverApi.getForEmployee(employeeId),
    enabled: !!employeeId && enabled,
    retry: false,
  });
// Chains drive DTR / overtime / evaluation routing, so refresh those too
const APPROVER_KEYS = [
  ["approvers"],
  ["approvalRules", "preview"],
  ["dtr"],
  ["dashboardMe"],
];
export const useSetEmployeeApprovers = () =>
  useKeysMutation(
    ({ employeeId, approverEmployeeIds, requestType }) =>
      approverApi.setForEmployee(employeeId, approverEmployeeIds, requestType),
    APPROVER_KEYS,
  );
export const useSetManyApprovers = () =>
  useKeysMutation(
    ({ employeeIds, approverEmployeeIds, requestType }) =>
      approverApi.setForMany(employeeIds, approverEmployeeIds, requestType),
    APPROVER_KEYS,
  );

/* ---------- Approval rules ---------- */
export const useApprovalRuleOptions = () =>
  useQuery({
    queryKey: ["approvalRules", "options"],
    queryFn: approvalRulesApi.getOptions,
    staleTime: 30 * 60 * 1000,
  });
export const useApprovalRules = (companyId, employeeType) =>
  useQuery({
    queryKey: ["approvalRules", "list", companyId, employeeType || "all"],
    queryFn: () => approvalRulesApi.getAll(companyId, employeeType),
    enabled: !!companyId,
  });
export const useApprovalPreview = (employeeId, type) =>
  useQuery({
    queryKey: ["approvalRules", "preview", employeeId, type || "all"],
    queryFn: () => approvalRulesApi.preview(employeeId, type),
    enabled: !!employeeId,
  });
// Rules change who reviews DTR / overtime / leave / validation right away
const RULE_KEYS = [
  ["approvalRules"],
  ["approvers"],
  ["dashboardMe"],
  ["overtime"],
  ["dtr"],
];
export const useSaveApprovalRule = () =>
  useKeysMutation(
    ({ companyId, type, levels, employeeType }) =>
      approvalRulesApi.save(companyId, type, levels, employeeType),
    RULE_KEYS,
  );
export const useResetApprovalRule = () =>
  useKeysMutation(
    ({ companyId, type, employeeType }) =>
      approvalRulesApi.reset(companyId, type, employeeType),
    RULE_KEYS,
  );

/* ---------- Org charts ---------- */
export const useBranchTree = (companyId, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["orgChart", "branches", companyId],
    queryFn: () => orgChartApi.getBranches(companyId),
    enabled: !!companyId && enabled,
  });
export const useRoleTree = (companyId, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["orgChart", "roles", companyId],
    queryFn: () => orgChartApi.getRoles(companyId),
    enabled: !!companyId && enabled,
  });

/* ---------- Overtime ---------- */
export const useOvertimeList = (filters, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["overtime", filters],
    queryFn: () => overtimeApi.getList(filters),
    placeholderData: keepPreviousData,
    enabled,
  });
export const useReviewOvertime = () =>
  useKeysMutation(
    ({ id, ...body }) => overtimeApi.review(id, body),
    [["overtime"], ["dashboardMe"], ["dtr"], ["attendance"]],
  );
export const useFileOvertimeOnBehalf = () =>
  useKeysMutation(
    (body) => overtimeApi.fileOnBehalf(body),
    [["overtime"], ["dashboardMe"]],
  );

/* ---------- Attendance corrections ---------- */
export const useCorrectionList = (filters, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["corrections", filters],
    queryFn: () => correctionsApi.getList(filters),
    placeholderData: keepPreviousData,
    enabled,
  });
// The last level changes the day: refresh attendance and DTR too
export const useReviewCorrection = () =>
  useKeysMutation(
    ({ id, ...body }) => correctionsApi.review(id, body),
    [["corrections"], ["dashboardMe"], ["dtr"], ["attendance"]],
  );

/* ---------- Client visits ---------- */
export const useClientVisits = (params) =>
  useQuery({
    queryKey: ["clientVisits", params],
    queryFn: () => clientVisitApi.getList(params),
    placeholderData: keepPreviousData,
  });
export const useReviewClientVisit = () =>
  useKeysMutation(
    ({ id, ...body }) => clientVisitApi.review(id, body),
    [["clientVisits"], ["dashboardMe"]],
  );
